/**
 * edit_nudge.ts — what one d-pad press does to the selected object in Edit mode.
 *
 * Pure, because this is exactly the kind of arithmetic that gets a sign wrong and
 * nobody notices until an editor pushes things the wrong way: the translation is
 * CAMERA-RELATIVE, so "right" on the d-pad is right on screen whichever way the
 * flying camera happens to face.
 */

/** The three tools a pad nudge can drive. */
export type NudgeTool = 'move' | 'rotate' | 'scale';

export interface Nudge {
  /** World-space translation (m). */
  dx: number; dz: number;
  /** Local rotation (rad) about up and about the camera's right. */
  dYaw: number; dPitch: number;
  /** Uniform scale MULTIPLIER (1 = unchanged). */
  scale: number;
}

/** Free-hand and snapped steps. Snapped matches the 1 m / 15° / 0.25 grid the
 *  X key toggles, so a pad and a mouse agree on what "snapped" means. */
export const NUDGE_M = 0.25;
export const NUDGE_SNAP_M = 1;
export const NUDGE_ROT = Math.PI / 12;
export const NUDGE_SCALE = 0.05;

/**
 * `nx`/`nz` are −1, 0 or +1 from the d-pad: +x is RIGHT on screen and +z is
 * AWAY from the camera (d-pad up), which is how a player reads a screen.
 *
 * The heading convention is the game's: forward = (−sin yaw, −cos yaw) and
 * right = (cos yaw, −sin yaw).
 */
export function editNudge(tool: NudgeTool, nx: number, nz: number, yaw: number, snapped: boolean): Nudge {
  const zero: Nudge = { dx: 0, dz: 0, dYaw: 0, dPitch: 0, scale: 1 };
  if (!nx && !nz) return zero;
  if (tool === 'rotate') return { ...zero, dYaw: nx * NUDGE_ROT, dPitch: nz * NUDGE_ROT };
  if (tool === 'scale') {
    // Up and right grow it, down and left shrink it — and the two are exact
    // inverses, so a nudge out and back leaves the object where it started
    // rather than a rounding error smaller.
    const n = nx - nz;
    return { ...zero, scale: n === 0 ? 1 : n > 0 ? (1 + NUDGE_SCALE) ** n : 1 / (1 + NUDGE_SCALE) ** -n };
  }
  const step = snapped ? NUDGE_SNAP_M : NUDGE_M;
  // right = (cos yaw, −sin yaw); away-from-camera = −forward = (sin yaw, cos yaw).
  return { ...zero, dx: (Math.cos(yaw) * nx + Math.sin(yaw) * nz) * step, dz: (-Math.sin(yaw) * nx + Math.cos(yaw) * nz) * step };
}
